import { useLayoutEffect } from "react";
import { Box, Container, Flex, Heading, Text } from "@radix-ui/themes";
import { Helmet } from "react-helmet-async";
import { TitleBlock } from "../TitleBlock";
import { SITE_HOST, siteUrl } from "../../lib/site";

export function PrivacyPage() {
  // Legal pages sit on plain white, not the home page's gradient (on <body>).
  useLayoutEffect(() => {
    document.body.classList.add("plain-page-bg");
    return () => document.body.classList.remove("plain-page-bg");
  }, []);

  return (
    <Container size="4" className="route-page">
      <Helmet>
        <title>Privacy Policy | Iris</title>
        <meta
          name="description"
          content="Learn how IrisChat handles your personal information and protects your privacy."
        />
        <link rel="canonical" href={siteUrl("/privacy/")} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={siteUrl("/privacy/")} />
        <meta property="og:title" content="Privacy Policy | Iris" />
        <meta
          property="og:description"
          content="Learn how IrisChat handles your personal information and protects your privacy."
        />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Privacy Policy | Iris" />
        <meta
          name="twitter:description"
          content="Learn how IrisChat handles your personal information and protects your privacy."
        />
      </Helmet>
      <Flex direction="column" gap="6">
        <TitleBlock title="Privacy Policy" />

        {/* Intro */}
        <Flex direction="column" gap="4">
          <Text size="3" className="route-body">
            IrisChat ("IrisChat", "we", "us," or "our") is developing a tool to
            support mental health and wellbeing. This Privacy Policy explains
            how we handle the personal information we collect from users of the
            IrisChat service.
          </Text>
          <Box className="route-card">
            <Flex direction="column" gap="3">
              <Text size="3" className="route-body">
                <strong>Important:</strong> IrisChat is not a medical or
                clinical service. Iris is not intended to diagnose, treat, cure,
                or prevent any disease or disorder, and is not a substitute for
                professional medical, psychiatric, or psychological care. If you
                are experiencing a mental health crisis or medical emergency,
                please contact emergency services (911), the 988 Suicide &amp;
                Crisis Lifeline (call or text 988), or the Crisis Text Line
                (text HOME to 741741) immediately. Do not use IrisChat to report
                an emergency. For more information about IrisChat's safety
                protocols, please visit{" "}
                <a href={siteUrl("/safety-protocol/")} className="route-link">
                  {`${SITE_HOST}/safety-protocol/`}
                </a>
                .
              </Text>
              <Text size="3" className="route-body">
                Use of IrisChat does not create a physician-patient,
                therapist-client, attorney-client, or any other privileged or
                confidential relationship.
              </Text>
              <Text size="3" className="route-body">
                Iris is powered by artificial intelligence. When you interact
                with Iris, you are communicating with an AI system, not a human
                being.
              </Text>
            </Flex>
          </Box>
        </Flex>

        {/* Personal Information We Collect */}
        <Box className="route-card">
          <Flex direction="column" gap="4">
            <Heading size="6">Personal Information We Collect</Heading>

            <Flex direction="column" gap="3">
              <Heading size="5">Personal information you provide</Heading>
              <ul className="route-list">
                <li>
                  <Text size="3" className="route-body">
                    Contact information (e.g., name, email address, phone
                    number)
                  </Text>
                </li>
                <li>
                  <Text size="3" className="route-body">
                    Demographic information (e.g., age, gender, occupation)
                  </Text>
                </li>
                <li>
                  <Text size="3" className="route-body">
                    Onboarding and wellbeing assessment responses (e.g.,
                    lifestyle surveys, emotional state, support preferences)
                  </Text>
                </li>
                <li>
                  <Text size="3" className="route-body">
                    Conversation and message content during your use of the
                    service
                  </Text>
                </li>
                <li>
                  <Text size="3" className="route-body">
                    Information you authorize sharing from third-party providers
                  </Text>
                </li>
              </ul>
            </Flex>

            <Flex direction="column" gap="3">
              <Heading size="5">
                Personal information we collect automatically or generate
              </Heading>
              <ul className="route-list">
                <li>
                  <Text size="3" className="route-body">
                    Usage and interaction data (e.g., feature usage, in-app
                    activity)
                  </Text>
                </li>
                <li>
                  <Text size="3" className="route-body">
                    Information derived from your interactions, used to
                    personalize your experience
                  </Text>
                </li>
                <li>
                  <Text size="3" className="route-body">
                    Device and technical information (e.g., IP address,
                    operating system, device type)
                  </Text>
                </li>
                <li>
                  <Text size="3" className="route-body">
                    Payment and billing information (processed by our payment
                    provider)
                  </Text>
                </li>
              </ul>
            </Flex>
          </Flex>
        </Box>

        {/* Sensitive Personal Information */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">Sensitive Personal Information</Heading>
            <Text size="3" className="route-body">
              IrisChat is a mental health and wellbeing service. In the course
              of using our service, you may share information relating to your
              mental health, emotional state, physical health, relationships,
              and personal history. We treat this information as sensitive and
              apply heightened protections to it.
            </Text>
            <Text size="3" className="route-body">
              Specifically:
            </Text>
            <ul className="route-list">
              <li>
                <Text size="3" className="route-body">
                  We do not use sensitive personal information to serve targeted
                  advertising.
                </Text>
              </li>
              <li>
                <Text size="3" className="route-body">
                  We do not sell or share sensitive personal information with
                  third parties for their own purposes.
                </Text>
              </li>
              <li>
                <Text size="3" className="route-body">
                  Access to sensitive personal information within IrisChat is
                  restricted to personnel who need it to operate or improve the
                  service.
                </Text>
              </li>
              <li>
                <Text size="3" className="route-body">
                  Where required by applicable law, we obtain your explicit
                  consent before processing sensitive personal information for
                  purposes beyond delivering the service.
                </Text>
              </li>
            </ul>
          </Flex>
        </Box>

        {/* How We Use Personal Information */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">How We Use Personal Information</Heading>
            <Text size="3" className="route-body">
              We use your personal information for the following purposes:
            </Text>
            <ul className="route-list">
              <li>
                <Text size="3" className="route-body">
                  <strong>Account management:</strong> Setting up and managing
                  your account, verifying your identity, and storing your
                  preferences and consents.
                </Text>
              </li>
              <li>
                <Text size="3" className="route-body">
                  <strong>Personalizing your experience:</strong> Customizing
                  content and providing personalized feedback based on your
                  engagement with the service.
                </Text>
              </li>
              <li>
                <Text size="3" className="route-body">
                  <strong>Service improvement:</strong> Analyzing usage patterns
                  and interactions to improve the functionality and
                  effectiveness of the service.
                </Text>
              </li>
              <li>
                <Text size="3" className="route-body">
                  <strong>Safety monitoring:</strong> Iris includes automated
                  safety features designed to detect certain types of
                  crisis-related content and, where detected, display mental
                  health and crisis resources. These automated systems are not
                  guaranteed to identify all situations that may require
                  intervention. You should not rely on Iris to recognize,
                  detect, or respond to a mental health crisis or medical
                  emergency. If you or someone you know is experiencing a mental
                  health crisis, suicidal thoughts, or a medical emergency,
                  please contact emergency services (911) or the 988 Suicide
                  &amp; Crisis Lifeline (call or text 988) immediately. Do not
                  use Iris to report an emergency. Safety monitoring is
                  performed by automated systems, not clinicians. Iris does not
                  include features to send alerts or notifications to health
                  care providers or other support persons.
                </Text>
              </li>
              <li>
                <Text size="3" className="route-body">
                  <strong>Legal compliance and rights protection:</strong>{" "}
                  Complying with applicable laws and protecting our rights and
                  yours.
                </Text>
              </li>
              <li>
                <Text size="3" className="route-body">
                  <strong>Fraud prevention and security:</strong> Identifying
                  and preventing unauthorized access and fraudulent activity.
                </Text>
              </li>
              <li>
                <Text size="3" className="route-body">
                  <strong>Business operations:</strong> Supporting operational,
                  customer support, and IT functions.
                </Text>
              </li>
            </ul>
          </Flex>
        </Box>

        {/* How We Use Your Data for Product Improvement */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">
              How We Use Your Data for Product Improvement
            </Heading>
            <Text size="3" className="route-body">
              We use information you provide through the app, including
              conversation content, to improve our products and services,
              conduct quality assurance, perform automated evaluations of our
              models, and support research and development.
            </Text>
            <Text size="3" className="route-body">
              For users who access the app through certain promotional or
              marketing channels, telemetry features may be enabled that allow
              our team to review conversation data in connection with these
              purposes. This conversation data may include the content of your
              interactions with the app and is not de-identified before use. We
              limit access to this data to authorized personnel who require it
              for the purposes described above.
            </Text>
            <Text size="3" className="route-body">
              Because this data is personal information, it is subject to your
              full privacy rights, including the right to request access,
              correction, and deletion. You may exercise these rights at any
              time by contacting us at{" "}
              <a href="mailto:privacy@irischat.ai" className="route-link">
                privacy@irischat.ai
              </a>
              .
            </Text>
            <Text size="3" className="route-body">
              We may also generate and use aggregate statistical data derived
              from usage patterns across our user base. Aggregate data reflects
              trends across groups of users and cannot be linked to any
              individual. This aggregate data is not subject to individual
              access or deletion requests.
            </Text>
          </Flex>
        </Box>

        {/* How We Disclose Personal Information */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">How We Disclose Personal Information</Heading>
            <Text size="3" className="route-body">
              We do not sell your personal information to third parties, and we
              do not share it for marketing or advertising purposes.
            </Text>
            <Text size="3" className="route-body">
              To operate our service, we may disclose personal information as
              follows:
            </Text>
            <ul className="route-list">
              <li>
                <Text size="3" className="route-body">
                  <strong>Analytics providers</strong> — to understand how the
                  service is used and improve it over time
                </Text>
              </li>
              <li>
                <Text size="3" className="route-body">
                  <strong>Onboarding and messaging platforms</strong> — to
                  manage your initial signup experience and deliver
                  communications
                </Text>
              </li>
              <li>
                <Text size="3" className="route-body">
                  <strong>Payment processors</strong> — to handle billing and
                  subscriptions
                </Text>
              </li>
              <li>
                <Text size="3" className="route-body">
                  <strong>AI technology providers</strong> — to deliver
                  conversational AI features; these providers are prohibited
                  from retaining your data or using it to train their models
                </Text>
              </li>
              <li>
                <Text size="3" className="route-body">
                  <strong>Session monitoring tools</strong> — to support the
                  quality and safety of AI interactions
                </Text>
              </li>
              <li>
                <Text size="3" className="route-body">
                  <strong>Professional advisors</strong> — lawyers, auditors,
                  and insurers as necessary to support our business
                </Text>
              </li>
              <li>
                <Text size="3" className="route-body">
                  <strong>Authorities and others</strong> — law enforcement or
                  government authorities where required by law or necessary to
                  protect rights and safety
                </Text>
              </li>
              <li>
                <Text size="3" className="route-body">
                  <strong>Business transferees</strong> — in connection with a
                  merger, acquisition, or similar transaction, in accordance
                  with applicable law
                </Text>
              </li>
            </ul>
            <Text size="3" className="route-body">
              Our service providers are contractually required to use your
              personal information only for the purposes we specify. A summary
              of the categories of trusted service providers we use is available
              upon request at{" "}
              <a href="mailto:privacy@irischat.ai" className="route-link">
                privacy@irischat.ai
              </a>
              . We update this list from time to time and will notify you of
              material changes.
            </Text>
          </Flex>
        </Box>

        {/* Children and Minors */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">Children and Minors</Heading>
            <Text size="3" className="route-body">
              IrisChat is intended for use by individuals 18 years of age and
              older. By accessing or using the Service, you represent and
              warrant that you are at least 18 years old. We do not knowingly
              collect personal information from anyone under the age of 18.
            </Text>
            <Text size="3" className="route-body">
              If you are under 18, please do not use IrisChat or provide any
              personal information through the service. AI companion services,
              including IrisChat, may not be suitable for minors.
            </Text>
            <Text size="3" className="route-body">
              If we learn that we have collected personal information from a
              minor under the age of 18, we will delete that information
              promptly. If you are a parent or guardian and believe your child
              has used IrisChat or provided us with personal information, please
              contact us at{" "}
              <a href="mailto:privacy@irischat.ai" className="route-link">
                privacy@irischat.ai
              </a>
              .
            </Text>
          </Flex>
        </Box>

        {/* Your Privacy Rights */}
        <Box className="route-card">
          <Flex direction="column" gap="4">
            <Heading size="6">Your Privacy Rights</Heading>
            <Text size="3" className="route-body">
              Your rights regarding your personal information depend on where
              you are located.
            </Text>

            <Flex direction="column" gap="3">
              <Heading size="5">California Residents (CCPA/CPRA)</Heading>
              <Text size="3" className="route-body">
                If you are a California resident, you have the following rights
                under the California Consumer Privacy Act (CCPA) and California
                Privacy Rights Act (CPRA):
              </Text>
              <ul className="route-list">
                <li>
                  <Text size="3" className="route-body">
                    <strong>Right to know:</strong> You may request information
                    about the categories and specific pieces of personal
                    information we have collected about you, the purposes for
                    which it is used, and the categories of third parties with
                    whom it is shared.
                  </Text>
                </li>
                <li>
                  <Text size="3" className="route-body">
                    <strong>Right to access:</strong> You may request a copy of
                    the personal information we have collected about you.
                  </Text>
                </li>
                <li>
                  <Text size="3" className="route-body">
                    <strong>Right to deletion:</strong> You may request that we
                    delete personal information we hold about you, subject to
                    certain exceptions.
                  </Text>
                </li>
                <li>
                  <Text size="3" className="route-body">
                    <strong>Right to correction:</strong> You may request that
                    we correct inaccurate personal information.
                  </Text>
                </li>
                <li>
                  <Text size="3" className="route-body">
                    <strong>Right to opt out of sale or sharing:</strong> We do
                    not sell or share personal information for cross-context
                    behavioral advertising.
                  </Text>
                </li>
                <li>
                  <Text size="3" className="route-body">
                    <strong>
                      Right to limit use of sensitive personal information:
                    </strong>{" "}
                    You may direct us to limit our use of your sensitive
                    personal information to only what is necessary to provide
                    the IrisChat service. To exercise this right, email us at{" "}
                    <a href="mailto:privacy@irischat.ai" className="route-link">
                      privacy@irischat.ai
                    </a>{" "}
                    with the subject line "Limit SPI Use."
                  </Text>
                </li>
                <li>
                  <Text size="3" className="route-body">
                    <strong>Right to non-discrimination:</strong> We will not
                    discriminate against you for exercising your CCPA/CPRA
                    rights.
                  </Text>
                </li>
              </ul>
              <Text size="3" className="route-body">
                To exercise your privacy rights, please email us at{" "}
                <a href="mailto:privacy@irischat.ai" className="route-link">
                  privacy@irischat.ai
                </a>
                . We will verify your identity before processing your request
                and respond within 45 days, with one possible 45-day extension
                where reasonably necessary.
              </Text>
            </Flex>

            <Flex direction="column" gap="3">
              <Heading size="5">All Other Users</Heading>
              <Text size="3" className="route-body">
                If you are located outside California, your rights regarding
                your personal information are subject to IrisChat's policies and
                practices as described in this Privacy Policy. If you reside in
                a U.S. state with an applicable consumer privacy law, we will
                honor rights granted under that law. We handle personal
                information of all users with care and in accordance with our
                internal standards. If you have questions about our privacy
                practices, you may contact us at{" "}
                <a href="mailto:privacy@irischat.ai" className="route-link">
                  privacy@irischat.ai
                </a>
                .
              </Text>
            </Flex>
          </Flex>
        </Box>

        {/* Privacy Questions */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">Privacy Questions</Heading>
            <Text size="3" className="route-body">
              For questions about this Privacy Policy or how we handle your
              personal information, please email us at{" "}
              <a href="mailto:privacy@irischat.ai" className="route-link">
                privacy@irischat.ai
              </a>
              . You will be connected with a member of our privacy team.
            </Text>
          </Flex>
        </Box>

        {/* Data Retention */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">Data Retention</Heading>
            <Text size="3" className="route-body">
              We retain your personal information for as long as your account is
              active or as needed to provide the service. Conversation and
              message content, including information derived from your
              conversations, is generally retained for up to twelve months to
              support your experience with the service. Content retained for
              product improvement, research, or model evaluation purposes may be
              retained for longer periods as described in the{" "}
              <em>How We Use Your Data for Product Improvement</em> section
              above. When you request deletion, we delete or de-identify your
              personal information across our active systems in accordance with
              our standard deletion processes. Residual copies may exist in
              backup or disaster-recovery systems and will be overwritten in the
              ordinary course of those systems' operation.
            </Text>
            <Text size="3" className="route-body">
              Some information may be retained longer where required or
              permitted by law, for example, payment and billing records for tax
              and financial compliance, and security logs for incident response
              purposes. We will confirm completion of your deletion request.
            </Text>
          </Flex>
        </Box>

        {/* Data Security */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">Data Security</Heading>
            <Text size="3" className="route-body">
              We employ commercially reasonable technical and organizational
              safeguards to protect personal information. However, no security
              measures are failsafe, and we cannot guarantee the security of
              your information.
            </Text>
            <Text size="3" className="route-body">
              Please contact us at{" "}
              <a href="mailto:privacy@irischat.ai" className="route-link">
                privacy@irischat.ai
              </a>{" "}
              with any security or privacy concerns.
            </Text>
          </Flex>
        </Box>

        {/* Changes to This Privacy Policy */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">Changes to This Privacy Policy</Heading>
            <Text size="3" className="route-body">
              This Privacy Policy is effective as of [effective date]. We may
              update this Privacy Policy from time to time to reflect changes in
              our practices, services, or applicable law. When we make material
              changes, we will notify you by posting the updated Privacy Policy
              on our website with a revised effective date and, where
              appropriate, by notifying you through the service or by email. We
              encourage you to review this Privacy Policy periodically.
            </Text>
          </Flex>
        </Box>

        {/* Do Not Track */}
        <Box className="route-card">
          <Flex direction="column" gap="3">
            <Heading size="6">Do Not Track</Heading>
            <Text size="3" className="route-body">
              IrisChat does not currently respond to "Do Not Track" browser
              signals. If we do so in the future, we will describe how in an
              updated version of this Privacy Policy.
            </Text>
          </Flex>
        </Box>
      </Flex>
    </Container>
  );
}
